import { Navigate, Route } from "react-router-dom";

import { AgentEditor } from "./Agents/AgentEditor";
import { AgentsTab } from "./Agents/AgentsTab";
import { AgentThreadsPage } from "./Agents/AgentThreadsPage";
import { LaunchAgent } from "./Agents/LaunchAgent";
import { AgentsTabs } from "./AgentsTabs";
import { ContextTab } from "./Context";
import { SkillDetail, Skills } from "./Skills";
import { AgentThreadDetail } from "./Threads/AgentThreadDetail";
import { ForkThreadRedirect } from "./Threads/ForkThreadRedirect";
import { ThreadsTab } from "./Threads/ThreadsTab";

/**
 * Route tree for the top-level Agents section.
 *
 * Centralized here (rather than declared inline in `Router.tsx`) so all
 * `/agents/*` routes live in one place — matches the convention used by
 * `ActionsRoutes`, `DatasetsRoutes`, etc.
 *
 * The Agents list page is a parent layout (`AgentsTabs`) with one child
 * route per tab, mounted via the parent's `<Outlet>`. Each tab's path
 * segment is the same string declared on `AgentTab` so the URL is the
 * single source of truth for the active tab. Adding a new tab is two
 * lines: a `<Route path>` here and a corresponding `AgentTab` enum value
 * + `<RobotoStyledTab>` in `AgentsTabs`.
 *
 * Detail / editor / launch / thread-detail / agent-threads routes are
 * siblings of the layout rather than children — each has its own chrome
 * (breadcrumb back to the list), not a body within the tab strip.
 * Sibling paths win over the layout's wildcard fallback because React
 * Router prefers more-specific static / dynamic segments to wildcards.
 */
export const AgentsRoutes = () => [
  <Route key={"layout"} element={<AgentsTabs />}>
    <Route index element={<Navigate to="library" replace />} />
    <Route path="library" element={<AgentsTab />} />
    <Route path="threads" element={<ThreadsTab />} />
    <Route path="skills" element={<Skills />} />
    <Route path="context" element={<ContextTab />} />
    <Route path="*" element={<Navigate to="library" replace />} />
  </Route>,
  // `new` and `:agentId/launch` outrank the catch-all `:agentId`
  // (static segments beat dynamic), so `new` / `launch` never match
  // as agent ids — declaration order is irrelevant here.
  <Route
    key={"agent-new"}
    path="new"
    element={<AgentEditor mode="create" />}
  />,
  <Route
    key={"agent-launch"}
    path=":agentId/launch"
    element={<LaunchAgent />}
  />,
  <Route
    key={"agent-detail"}
    path=":agentId"
    element={<AgentEditor mode="edit" />}
  />,
  // A skill's own page, and one pinned to a specific version. Both outrank
  // the layout's `*` fallback (static and dynamic segments both score above
  // a splat), so `/agents/skills/sk_123` reaches the detail page rather than
  // redirecting to the Library tab. `skills` stays a layout child, so
  // `/agents/skills` is still the list.
  <Route
    key={"skill-detail"}
    path="skills/:skillId"
    element={<SkillDetail />}
  />,
  <Route
    key={"skill-version-detail"}
    path="skills/:skillId/versions/:version"
    element={<SkillDetail />}
  />,
  // React Router ranks matches by specificity score, not declaration
  // order, so the extra static `fork` segment makes this outrank
  // `threads/:threadId` regardless of where it sits in the array.
  // Navigating here forks the thread and replaces the URL with the new
  // fork's detail page.
  <Route
    key={"thread-fork"}
    path="threads/:threadId/fork"
    element={<ForkThreadRedirect />}
  />,
  <Route
    key={"threads-detail"}
    path="threads/:threadId"
    element={<AgentThreadDetail />}
  />,
  // Ties with `threads/:threadId` in React Router's ranking; the only
  // URL matching both is the degenerate `/agents/threads/threads`
  // (impossible with real `agt_` / `ags_` ids). Declared second so the
  // tie resolves to thread detail.
  <Route
    key={"agent-detail-threads"}
    path=":agentId/threads"
    element={<AgentThreadsPage />}
  />,
];
